import { FC, useState } from "react";
import { Box, Fade, Typography } from "@mui/material";
import { makeSxStyles } from "ui";
import { DateTime } from "luxon";
import {
  AdminGetRoutesLocationsResDto,
  RouteStartingPointDto,
} from "service/api/estabrookSchemas";
import { formatStartTimeLabel } from "../../../utils/date-time";
import { getActualStartTime } from "../../../utils/route-planner";
import useRoutePlannerStore from "../../../state/route-planner-store";
import { RouteStatus } from "./helpers/route-status.enum";

export interface DriverStartingPointDetailsProps {
  startingPoint?: RouteStartingPointDto;
  driverFullName?: string;
  currentRouteStatus?: RouteStatus;
  routeStartTime?: string;
  locations?: AdminGetRoutesLocationsResDto[];
}

interface DriverStartingPointTooltipProps {
  isHovering: boolean;
  position: { x: number; y: number };
  details: RouteStartingPointDto | null;
  driverFullName?: string;
  // Scheduled route start time as "HH:mm"
  startTime?: string;
  // ISO time the driver started their first stop; shown instead of the scheduled time once set
  actualStartTime?: string;
}

export const DriverStartingPointTooltip: FC<
  DriverStartingPointTooltipProps
> = ({
  isHovering,
  position,
  details,
  driverFullName,
  startTime,
  actualStartTime,
}) => {
  const styles = useStyles();
  if (!details) return null;

  let displayedStartTime: DateTime | null = null;
  if (actualStartTime) {
    displayedStartTime = DateTime.fromISO(actualStartTime);
  } else if (startTime) {
    displayedStartTime = DateTime.fromFormat(startTime, "HH:mm");
  }

  return (
    <Fade in={isHovering && !!driverFullName} timeout={300}>
      <Box
        sx={{
          ...styles.hoverBox,
          left: position.x,
          top: position.y - 16,
          transform: "translate(-50%, -100%)",
          position: "fixed",
        }}
      >
        <Typography sx={styles.tooltipText}>{details.address}</Typography>
        {driverFullName && <Typography>{driverFullName}</Typography>}
        {displayedStartTime?.isValid && (
          <Typography sx={{ marginTop: "8px" }}>
            Start time: {formatStartTimeLabel(displayedStartTime)}
          </Typography>
        )}
      </Box>
    </Fade>
  );
};

const DriverStartingPointDetails: FC<DriverStartingPointDetailsProps> = (
  props,
) => {
  const { setDriverMapCentering } = useRoutePlannerStore();
  const {
    startingPoint,
    driverFullName,
    currentRouteStatus,
    routeStartTime,
    locations,
  } = props;
  const styles = useStyles();
  const [isHovering, setIsHovering] = useState(false);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });

  const handleMouseEnter = (event: React.MouseEvent) => {
    setHoverPosition({ x: event.clientX, y: event.clientY });
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  return (
    <>
      <Box
        sx={styles.addressWrapper}
        onMouseEnter={handleMouseEnter}
        onMouseMove={(event) =>
          setHoverPosition({ x: event.clientX, y: event.clientY })
        }
        onMouseLeave={handleMouseLeave}
        onClick={() => {
          if (currentRouteStatus !== RouteStatus.Live) return;
          setDriverMapCentering(startingPoint);
        }}
      >
        <Box sx={styles.startingPoint} />
        <Box sx={styles.startingPointLine} />
      </Box>
      {startingPoint && (
        <DriverStartingPointTooltip
          isHovering={isHovering}
          position={hoverPosition}
          details={startingPoint}
          driverFullName={driverFullName}
          startTime={routeStartTime}
          actualStartTime={getActualStartTime(locations, currentRouteStatus)}
        />
      )}
    </>
  );
};

const useStyles = makeSxStyles((theme) => ({
  addressWrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    cursor: "pointer",
  },
  startingPoint: {
    borderRadius: "100%",
    width: "8px",
    height: "8px",
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  startingPointLine: {
    content: '""',
    width: "4px",
    height: "1px",
    display: "inline-flex",
    backgroundColor: "#fff",
  },
  tooltipText: {
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 600,
    color: "rgba(138, 206, 255, 1)",
    mb: "4px",
    maxWidth: "240px",
  },
  hoverBox: {
    backgroundColor: theme.palette.grey["600"],
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.06)",
    padding: "16px",
    borderRadius: "12px",
    zIndex: 1001,
    minWidth: "240px",
    maxWidth: "400px",
    minHeight: "100px",

    "&::before": {
      content: '""',
      position: "absolute",
      width: 0,
      height: 0,
      borderLeft: "8px solid transparent",
      borderRight: "8px solid transparent",
      borderTop: "8px solid #282A2D",
      bottom: "-8px",
      left: "50%",
      transform: "translateX(-50%)",
    },
  },
}));

export default DriverStartingPointDetails;
